import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Truck, Plus, Trash2, Pencil, Fuel, Gauge } from "lucide-react";
import { toast } from "sonner";

const empty = { plate: "", name: "", tank_capacity_l: 70, km_per_tank: 500, note: "" };

export default function Vehicles() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/vehicles").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/vehicles/${editing.id}`, form);
      else await api.post("/vehicles", form);
      toast.success(editing ? "Mezzo aggiornato" : "Mezzo aggiunto");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (v) => {
    if (!confirm(`Eliminare ${v.plate}?`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success("Mezzo eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Flotta</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Mezzi / Furgoni</h1>
          <p className="text-slate-500 mt-2 text-sm">Registra ogni furgone con targa, capacità serbatoio e km con un pieno per il calcolo anomalie carburante.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
          className="bg-sky-600 hover:bg-sky-700 text-white h-11" data-testid="create-vehicle-button">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Mezzo
        </Button>
      </header>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="vehicles-list">
        {list.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-400">
            Nessun mezzo registrato.
          </div>
        )}
        {list.map((v) => (
          <div key={v.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`vehicle-${v.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
                <Truck className="w-5 h-5" />
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(v); setForm({ ...empty, ...v }); setOpen(true); }}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`edit-vehicle-${v.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(v)} className="p-2 rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-600"
                  data-testid={`delete-vehicle-${v.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-4">
              <div className="font-mono font-black text-xl text-slate-900 tracking-wider">{v.plate}</div>
              <div className="text-sm text-slate-500 mt-0.5">{v.name}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-5 text-xs">
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                <Fuel className="w-4 h-4 text-sky-600" />
                <div>
                  <div className="text-slate-400 text-[10px] uppercase font-semibold">Serbatoio</div>
                  <div className="font-mono font-bold">{v.tank_capacity_l} L</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                <Gauge className="w-4 h-4 text-sky-600" />
                <div>
                  <div className="text-slate-400 text-[10px] uppercase font-semibold">Km / pieno</div>
                  <div className="font-mono font-bold">{v.km_per_tank}</div>
                </div>
              </div>
            </div>
            {v.note && <div className="mt-3 text-xs text-slate-500 italic">{v.note}</div>}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">
              {editing ? "Modifica Mezzo" : "Nuovo Mezzo"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="vehicle-form">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Targa</Label>
                <Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} required data-testid="vehicle-plate-input" />
              </div>
              <div><Label>Modello / Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="vehicle-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Capacità serbatoio (L)</Label>
                <Input type="number" step="0.1" value={form.tank_capacity_l} onChange={(e) => setForm({ ...form, tank_capacity_l: Number(e.target.value) })} data-testid="vehicle-tank-input" />
              </div>
              <div><Label>Km con un pieno</Label>
                <Input type="number" step="1" value={form.km_per_tank} onChange={(e) => setForm({ ...form, km_per_tank: Number(e.target.value) })} data-testid="vehicle-kmtank-input" />
              </div>
            </div>
            <div><Label>Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="save-vehicle-button">
                {editing ? "Salva" : "Crea"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
